/**
 * What a promotion's add-all button sends, as pure functions: the transformers
 * `useAddProductFromPromotion` runs, kept out of the hooks barrel so the stock
 * check can ask the same question without it becoming public API.
 */
import {
  PromotionType,
  ComplexPromotionTypes,
  IPackage,
} from '../../graphQL/commons'
import { getPromotionFields } from '../common/getPromotionFields'
import type { IAddPrefillCartItem } from '../../hooks/prefillCart/useAddPrefillCart'
import {
  ICommonPromotionFields,
  IConditionAction,
  IProduct,
  IPromotionShortcutData,
} from '../../interfaces'

interface ITransformerItemsArgs {
  currentItems: IAddPrefillCartItem[]
  autoAdd: boolean
  promotionsFields: ICommonPromotionFields
  selectedProduct?: IProduct
  groupIdentifier?: string
}

interface IQuantityPackage {
  quantity: number
  package: IPackage | null
}

export const EmptyPackage: IPackage = {
  name: '',
  label: '',
  quantity: 0,
  price: 0,
  jumpQty: 0,
  minQty: null,
  maxQty: null,
  stock: null,
  outOfStock: null,
}
/**
 * Array of conditional transformer objects for building the list of cart items
 * to add from a promotion, depending on the promotion type, selection state, and group alignment.
 * Each entry contains a `condition` to match the current context, and a `transformAction`
 * to produce an updated list of cart items.
 *
 * The array is composed of IConditionAction<ITransformerItemsArgs, IAddPrefillCartItem[]> objects.
 */
const transformersItems: IConditionAction<
  ITransformerItemsArgs,
  IAddPrefillCartItem[]
>[] = [
  /**
   * Handles COMBO promotions where a specific product is selected, and there is no package specified.
   * Adds the selected product's SKU to the cart, using the defined buy quantity, with no package info.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: null,
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions where a specific product and a buy package are selected.
   * Adds the selected product's SKU and the specified package to the cart, using buy quantity.
   */
  {
    condition: ({ promotionsFields: { type, customerBuysPackage }, selectedProduct }) =>
      type === PromotionType.COMBO && !!selectedProduct && !!customerBuysPackage,
    transformAction: ({
      selectedProduct,
      promotionsFields: { customerBuysQuantity: quantity, customerBuysPackage },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku: selectedProduct!.sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerBuysPackage!,
        },
        price: selectedProduct?.price || 0,
        name: selectedProduct?.name || '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) for "customer gets" combo options.
   * Adds the first `customerGetsItems` SKU and its defined quantity, no package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity && !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions (autoAdd OFF) when a get package is specified.
   * Adds the first gets SKU and its quantity, with the specified package, to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerGetsItems, customerGetsQuantity, customerGetsPackage },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !autoAdd &&
      !!customerGetsItems.length &&
      !!customerGetsQuantity &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsQuantity: quantity,
        customerGetsItems: [sku],
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled and a percentage discount active.
   * Adds the first gets SKU and its quantity (no package) to the cart automatically.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: null,
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMBO promotions with autoAdd enabled, a percentage discount, and a gets package.
   * Adds the first gets SKU and its quantity, and also sets the gets package.
   */
  {
    condition: ({
      promotionsFields: {
        type,
        customerGetsItems,
        customerGetsQuantity,
        customerGetsPercentage,
        customerGetsPackage,
      },
      autoAdd,
    }) =>
      type === PromotionType.COMBO &&
      !!autoAdd &&
      !!customerGetsQuantity &&
      !!customerGetsItems.length &&
      !!customerGetsPercentage &&
      !!customerGetsPackage,
    transformAction: ({
      promotionsFields: {
        customerGetsItems: [sku],
        customerGetsQuantity,
        customerGetsPackage,
      },
      currentItems,
    }) => [
      ...currentItems,
      {
        sku,
        quantity: customerGetsQuantity,
        package: {
          ...EmptyPackage,
          name: customerGetsPackage!,
        },
        price: 0,
        name: '',
      },
    ],
  },
  /**
   * Handles COMPLEX promotions where there is exactly one buys group and one gets group.
   * Adds all SKUs from the only buys group to the cart, ignoring missing SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerBuysGroups[0].items
          .filter(({ sku }) => sku !== null)
          .map(({ sku, quantity }) => ({
            sku,
            quantity,
            package: null,
            price: 0,
            name: '',
          })),
      ]
    },
  },
  /**
   * Handles COMPLEX promotions with multiple buys groups, but only one gets group.
   * Appends all SKUs from the target buys group (matching identifier) to the cart, skipping null SKUs.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length > 1 &&
      customerGetsGroups.length === 1,
    transformAction: ({
      promotionsFields: { customerBuysGroups },
      currentItems,
      groupIdentifier,
    }) => {
      const group = customerBuysGroups.find(
        (group) => group.identifier === groupIdentifier,
      )
      if (group) {
        return [
          ...currentItems,
          ...group.items
            .filter(({ sku }) => sku !== null)
            .map(({ sku, quantity }) => ({
              sku,
              quantity,
              package: null,
              price: 0,
              name: '',
            })),
        ]
      }
      return currentItems
    },
  },
  /**
   * Handles COMPLEX promotions (with exactly one buys group and one gets group),
   * when the gets group is either:
   * (A) Type is not DIRECT_PERCENTAGE and autoAdd is disabled, or
   * (B) Type is PERCENTAGE and autoAdd is enabled.
   * Adds all SKUs from the gets group to the cart.
   */
  {
    condition: ({
      promotionsFields: { type, customerBuysGroups, customerGetsGroups },
      autoAdd,
    }) =>
      type === PromotionType.COMPLEX &&
      customerBuysGroups.length === 1 &&
      customerGetsGroups.length === 1 &&
      ((customerGetsGroups[0].type !==
        ComplexPromotionTypes.DIRECT_PERCENTAGE &&
        !autoAdd) ||
        (customerGetsGroups[0].type === ComplexPromotionTypes.PERCENTAGE &&
          !!autoAdd)),
    transformAction: ({
      promotionsFields: { customerGetsGroups },
      currentItems,
    }) => {
      return [
        ...currentItems,
        // Only support one customerBuys group and one customerGets group
        ...customerGetsGroups[0].items
          .filter(({ sku }) => sku !== null)
          .map(
            ({ sku, quantity }) =>
              ({
                sku,
                quantity,
                package: null,
              }) as IAddPrefillCartItem,
          ),
      ]
    },
  },
  /**
   * Deduplication step. Normalizes the cart items list by merging any
   * items which share the same SKU and package name, summing quantities together.
   */
  {
    condition: ({ currentItems: { length } }) => length > 1,
    transformAction: ({ currentItems }) => {
      const separator = '+';
      const mapQuantity = currentItems.reduce<Record<string, IQuantityPackage>>(
        (acc, item) => {
          const { sku, quantity, package: packageData } = item
          const packageName = packageData?.name || ''
          const identifier = `${sku}${packageName ? `${separator}${packageName}` : ''}`
          return {
            ...acc,
            [identifier]: {
              quantity: (acc[identifier]?.quantity || 0) + quantity,
              package: packageData
            },
          }
        },
        {},
      )
      const result = Object.keys(mapQuantity).map<IAddPrefillCartItem>(
        (identifier) => {
          const [sku] = identifier.split(separator)
          const { package: packageData, quantity } = mapQuantity[identifier]
          return {
            sku,
            quantity,
            package: packageData,
            price: 0,
            name: '',
          }
        },
      )
      return result
    },
  },
]

interface IGetPromotionItemsToAddArgs extends IPromotionShortcutData {
  autoAdd: boolean
  selectedProduct?: IProduct
}

/**
 * The cart lines the add-all button sends for a promotion — the pure half of
 * `addProductFromPromotion`, exported so the stock check can ask what a
 * button would add rather than re-deriving it.
 */
export const getPromotionItemsToAdd = ({
  promotion,
  groupIdentifier,
  autoAdd,
  selectedProduct,
}: IGetPromotionItemsToAddArgs): IAddPrefillCartItem[] => {
  if (!promotion) return []
  const transformItemsArgs: ITransformerItemsArgs = {
    currentItems: [],
    autoAdd,
    promotionsFields: getPromotionFields(promotion),
    selectedProduct,
  }

  return transformersItems.reduce<IAddPrefillCartItem[]>(
    (acc, { condition, transformAction }) =>
      condition({
        ...transformItemsArgs,
        currentItems: acc,
        groupIdentifier: groupIdentifier || '',
      })
        ? transformAction({
            ...transformItemsArgs,
            currentItems: acc,
            groupIdentifier: groupIdentifier || '',
          })
        : acc,
    [],
  )
}
